import { useState, useMemo } from "react";
import { useLang } from "@/i18n/LangContext";
import { sanitizeHtml } from "@/lib/bbcode";

interface SpecRow {
  label: string;
  value: string;
  icon: React.ReactNode;
}

function parseRequirements(html: string): SpecRow[] {
  const specs: SpecRow[] = [];
  const text = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");

  const osIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
  );
  const processorIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M15 2v2M9 2v2M15 20v2M9 20v2M2 15h2M2 9h2M20 15h2M20 9h2" /></svg>
  );
  const memoryIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 6V4M10 6V4M14 6V4M18 6V4" /></svg>
  );
  const graphicsIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 3h4v4M22 3L15 10" /></svg>
  );
  const directxIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
  );
  const storageIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
  );
  const networkIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>
  );

  const iconMap: Record<string, React.ReactNode> = {
    "운영 체제": osIcon,
    "OS": osIcon,
    "프로세서": processorIcon,
    "Processor": processorIcon,
    "메모리": memoryIcon,
    "Memory": memoryIcon,
    "그래픽": graphicsIcon,
    "Graphics": graphicsIcon,
    "DirectX": directxIcon,
    "저장 공간": storageIcon,
    "Storage": storageIcon,
    "네트워크": networkIcon,
    "Network": networkIcon,
  };

  const defaultIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
  );

  const lines = text.split("\n");
  for (const line of lines) {
    const match = line.match(/^(.+?):\s*(.+)$/);
    if (match) {
      const label = match[1].trim();
      const value = match[2].trim();
      if (
        label &&
        value &&
        !label.includes("최소") &&
        !label.includes("권장") &&
        !label.includes("64비트") &&
        !label.includes("Minimum") &&
        !label.includes("Recommended") &&
        !label.includes("64-bit")
      ) {
        const icon = Object.entries(iconMap).find(([key]) => label.includes(key))?.[1] || defaultIcon;
        specs.push({ label, value, icon });
      }
    }
  }
  return specs;
}

export default function SystemRequirements({ minimum, recommended }: { minimum: string; recommended?: string }) {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState<"min" | "rec">(recommended ? "rec" : "min");
  const minSpecs = useMemo(() => parseRequirements(minimum), [minimum]);
  const recSpecs = useMemo(() => (recommended ? parseRequirements(recommended) : []), [recommended]);
  const specs = activeTab === "rec" ? recSpecs : minSpecs;

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm mb-6 overflow-hidden">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-6 pt-5 pb-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.systemRequirements}</h2>
        </div>
        {recommended && (
          <div className="flex bg-hover-bg dark:bg-background rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab("min")}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
                activeTab === "min"
                  ? "bg-card-bg dark:bg-card-bg text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-muted hover:text-gray-700 dark:hover:text-foreground"
              }`}
            >
              {t.gameDetail.minimum}
            </button>
            <button
              onClick={() => setActiveTab("rec")}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
                activeTab === "rec"
                  ? "bg-card-bg dark:bg-card-bg text-gray-900 dark:text-white shadow-sm"
                  : "text-gray-500 dark:text-muted hover:text-gray-700 dark:hover:text-foreground"
              }`}
            >
              {t.gameDetail.recommended}
            </button>
          </div>
        )}
      </div>

      {/* 스펙 그리드 */}
      <div className="p-6">
        {specs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {specs.map((spec, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-inner-bg dark:bg-inner-bg rounded-xl px-4 py-3.5 border border-inner-border dark:border-card-border"
              >
                <div className="shrink-0 w-9 h-9 rounded-lg bg-card-bg dark:bg-card-bg border border-gray-200 dark:border-card-border flex items-center justify-center text-gray-500 dark:text-muted">
                  {spec.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold text-gray-400 dark:text-muted uppercase tracking-wider mb-0.5">
                    {spec.label}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug break-words">
                    {spec.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="text-sm text-gray-600 dark:text-muted leading-7 [&_ul]:list-disc [&_ul]:pl-4 [&_strong]:text-gray-900 dark:[&_strong]:text-white"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeTab === "rec" && recommended ? recommended : minimum) }}
          />
        )}
      </div>
    </div>
  );
}
